import { TerraformProviderDoc, TerraformProviderDocType } from "types/generated";

export type ProviderDocTreeNode = {
  id: string;
  parentId: string | null;
  name: string;
  markdown: string | null;
  key?: string;
  type?: TerraformProviderDocType;
};

/**
 * Transforms API doc metadata into a tree structure for TreeList component
 * Overview is always placed first, followed by categories, then other docs
 */
export const transformApiDocsToTree = (
  files: TerraformProviderDoc[],
  overviewContent?: string
): ProviderDocTreeNode[] => {
  const categories = createCategoriesFromFiles(files);
  const allDocs = files.map((file) => transformFileToTreeNode(file, overviewContent));

  // Separate overview from other docs
  const overviewDoc = allDocs.find((doc) => doc.type === TerraformProviderDocType.Overview);
  const otherDocs = allDocs.filter((doc) => doc.type !== TerraformProviderDocType.Overview);

  // Order: overview first, then categories, then other docs
  return [
    ...(overviewDoc ? [overviewDoc] : []),
    ...categories,
    ...otherDocs,
  ];
};

/**
 * Creates category nodes from unique doc types in files
 */
const createCategoriesFromFiles = (files: TerraformProviderDoc[]): ProviderDocTreeNode[] => {
  const types = new Set(files.map((f) => f.type));
  return Array.from(types)
    .filter((type) => type !== TerraformProviderDocType.Overview)
    .map((type) => ({
      id: getCategoryId(type),
      parentId: null,
      name: getCategoryName(type),
      markdown: null,
    }));
};

/**
 * Transforms a single API doc file to a tree node
 */
const transformFileToTreeNode = (
  file: TerraformProviderDoc,
  overviewContent?: string
): ProviderDocTreeNode => {
  const isOverview = file.type === TerraformProviderDocType.Overview;

  return {
    id: parseIdFromKey(file.key),
    parentId: isOverview ? null : getCategoryId(file.type),
    name: isOverview ? "Overview" : parseNameFromFilename(file.filename),
    markdown: isOverview && overviewContent ? overviewContent : null,
    key: file.key,
    type: file.type,
  };
};

/**
 * Converts doc type to category ID
 */
export const getCategoryId = (type: TerraformProviderDocType): string => {
  switch (type) {
    case TerraformProviderDocType.Overview:
      return "overview";
    case TerraformProviderDocType.Resource:
      return "resources";
    case TerraformProviderDocType.DataSource:
      return "data-sources";
    case TerraformProviderDocType.Unknown:
      return "other";
    default:
      return "other";
  }
};

/**
 * Converts doc type to human-readable category name
 */
const getCategoryName = (type: TerraformProviderDocType): string => {
  switch (type) {
    case TerraformProviderDocType.Overview:
      return "Overview";
    case TerraformProviderDocType.Resource:
      return "Resources";
    case TerraformProviderDocType.DataSource:
      return "Data Sources";
    case TerraformProviderDocType.Unknown:
      return "Other";
    default:
      return "Other";
  }
};

/**
 * Parses display name from filename
 * "example_instance.md" => "example_instance"
 */
const parseNameFromFilename = (filename: string): string => {
  return filename.replace(/\.md$/, "");
};

/**
 * Parses tree node ID from doc key
 * "docs/resources/example_instance.md" => "resources/example_instance"
 * "docs/overview.md" => "overview"
 */
const parseIdFromKey = (key: string): string => {
  return key.replace(/^docs\//, "").replace(/\.md$/, "");
};

/**
 * Find a doc node by its ID in the tree
 */
export const findDocByKey = (
  docs: ProviderDocTreeNode[],
  key: string
): ProviderDocTreeNode | undefined => {
  return docs.find((d) => d.key === key);
};

/**
 * Find a doc node by its tree ID (slug)
 */
export const findDocById = (
  docs: ProviderDocTreeNode[],
  id: string
): ProviderDocTreeNode | undefined => {
  return docs.find((d) => d.id === id);
};
